import { jsx, jsxs } from "react/jsx-runtime";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useTheme, Box } from "@mui/material";
import { clsx } from "../../utils/mergeProps.es.js";
import { containsChild, SlotProvider, hasContentSlot } from "../../utils/slots.es.js";
import EzCard from "../EzCard/EzCard.es.js";
import EzContent from "../EzContent/EzContent.es.js";
import EzFooter from "../EzContent/EzFooter.es.js";
import EzHeader from "../EzContent/EzHeader.es.js";
import EzHeading from "../EzHeading/EzHeading.es.js";
import EzLayout from "../EzLayout/EzLayout.es.js";
import stitches from "../theme.config.es.js";
import en from "./en.es.js";
import { ScrollLock } from "./ScrollLock.es.js";
import EzPortal from "../EzPortal/EzPortal.es.js";
import FocusScope from "../utils/FocusScope/FocusScope.es.js";
import EzButton from "../EzButton/EzButton.es.js";
import EzIconButton from "../EzIconButton/EzIconButton.es.js";
import useTranslation from "../../utils/hooks/useTranslation.es.js";
import EzIcon from "../EzIcon/EzIcon.es.js";
import { useId } from "@radix-ui/react-id";
const HEADER_ID = "modal-header";
const SUBMIT_BUTTON_ID = "modal-submit-button";
const DISMISS_BUTTON_ID = "modal-dismiss-button";
const CLOSE_BUTTON_ID = "modal-close-button";
const CARD_ID = "modal-card";
const MODAL_IDS = [HEADER_ID, SUBMIT_BUTTON_ID, DISMISS_BUTTON_ID, CLOSE_BUTTON_ID, CARD_ID];
const overlay = stitches.css({
  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "$modal-overlay-bg",
  zIndex: "$modal-z"
  // this is hard-coded in theme.config.ts for now, but we should really pull it in from mui when we convert modals to mui
});
const dialog = stitches.css({
  border: "0 !important",
  height: "$full",
  width: "$full",
  outline: "none",
  overflowY: "auto",
  // IE fix to prevent flex items overflowing. See: https://github.com/philipwalton/flexbugs#flexbug-3
  position: "relative",
  // show when the modal has focus
  "&:focus": { boxShadow: "$focus-ring" },
  "@medium": {
    height: "auto",
    maxHeight: "$modal-height",
    "&&": { width: "$modal-width" }
  }
});
const header = stitches.css({
  justifyContent: "space-between",
  "& + *": { borderTop: "1px solid $modal-border" },
  "&&": { padding: "$modal-tray-py $modal-tray-px" },
  "@medium": {
    "& + *": { borderStyle: "none" },
    "&&": { padding: "$modal-py $modal-px" }
  }
});
const body = stitches.css({
  flexGrow: 1,
  overflowY: "auto",
  "&&": { padding: "$modal-tray-py $modal-tray-px" },
  "@medium": { "&&": { padding: "0 $modal-px $modal-py" } }
});
const footer = stitches.css({
  "&&": { padding: "$modal-tray-py $modal-tray-px" },
  "@medium": { "&&": { padding: "$modal-py $modal-px" } }
});
const useDialogBackdrop = ({ onDismiss }) => ({
  onClick: (e) => e.target === e.currentTarget && onDismiss()
});
const useDialog = ({ onDismiss }) => ({
  role: "dialog",
  "aria-modal": true,
  tabIndex: -1,
  onKeyDown: (event) => {
    if (event.defaultPrevented) return;
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onDismiss();
  }
});
const EzModal = ({
  children,
  headerText,
  destructive,
  dismissLabel,
  isOpen,
  isSubmitting,
  onDismiss: onDismissHandler,
  onSubmit,
  overrideDefaultFocus,
  submitLabel,
  slotProps
}) => {
  var _a, _b, _c, _d, _e;
  const labelId = useId();
  const isDismissable = Boolean(onDismissHandler);
  const onDismiss = isDismissable ? onDismissHandler : () => {
  };
  const backdrop = useDialogBackdrop({ onDismiss });
  const dialogProps = useDialog({ onDismiss });
  const { t } = useTranslation(en);
  const emotionTheme = useTheme();
  const hasPicture = containsChild(children, "EzPreview");
  if (!isOpen) return null;
  return /* @__PURE__ */ jsx(EzPortal, { children: /* @__PURE__ */ jsxs(
    "div",
    {
      ...slotProps == null ? void 0 : slotProps.overlay,
      className: clsx(overlay(), (_a = slotProps == null ? void 0 : slotProps.overlay) == null ? void 0 : _a.className),
      ...backdrop,
      children: [
        /* @__PURE__ */ jsx(ScrollLock, {}),
        /* @__PURE__ */ jsx(FocusScope, { contain: true, restoreFocus: true, autoFocus: !overrideDefaultFocus, modalIds: MODAL_IDS, children: /* @__PURE__ */ jsx(
          SlotProvider,
          {
            slots: {
              header: {
                ...slotProps == null ? void 0 : slotProps.header,
                className: clsx(header(), (_b = slotProps == null ? void 0 : slotProps.header) == null ? void 0 : _b.className)
              },
              content: {
                ...slotProps == null ? void 0 : slotProps.content,
                className: clsx(body(), (_c = slotProps == null ? void 0 : slotProps.content) == null ? void 0 : _c.className)
              },
              footer: {
                ...slotProps == null ? void 0 : slotProps.footer,
                className: clsx(footer(), (_d = slotProps == null ? void 0 : slotProps.footer) == null ? void 0 : _d.className)
              }
            },
            children: /* @__PURE__ */ jsxs(
              EzCard,
              {
                ...slotProps == null ? void 0 : slotProps.dialog,
                ...dialogProps,
                className: clsx(dialog(), (_e = slotProps == null ? void 0 : slotProps.dialog) == null ? void 0 : _e.className),
                "aria-labelledby": labelId,
                id: CARD_ID,
                children: [
                  headerText && /* @__PURE__ */ jsx(EzHeader, { children: /* @__PURE__ */ jsx(EzHeading, { size: "2", id: labelId, children: /* @__PURE__ */ jsx(Box, { id: HEADER_ID, color: emotionTheme.tokens["color-text-default"], children: headerText }) }) }),
                  hasContentSlot(children) ? children : /* @__PURE__ */ jsx(EzContent, { children }),
                  (submitLabel || dismissLabel) && /* @__PURE__ */ jsx(EzFooter, { children: /* @__PURE__ */ jsxs(EzLayout, { layout: { base: "stack", medium: "basic" }, children: [
                    submitLabel && /* @__PURE__ */ jsx(
                      EzButton,
                      {
                        id: SUBMIT_BUTTON_ID,
                        color: destructive ? "destructive" : void 0,
                        onClick: onSubmit,
                        loading: isSubmitting,
                        children: submitLabel
                      }
                    ),
                    dismissLabel && /* @__PURE__ */ jsx(
                      EzButton,
                      {
                        id: CLOSE_BUTTON_ID,
                        disabled: isSubmitting,
                        onClick: onDismiss,
                        variant: onSubmit ? "outlined" : "filled",
                        children: dismissLabel
                      }
                    )
                  ] }) }),
                  isDismissable && /* @__PURE__ */ jsx(
                    Box,
                    {
                      id: CLOSE_BUTTON_ID,
                      position: hasPicture ? "absolute" : void 0,
                      right: hasPicture ? 0 : void 0,
                      top: hasPicture ? 0 : void 0,
                      margin: emotionTheme.tokens["spacing-5"],
                      gridArea: hasPicture ? void 0 : "close",
                      sx: {
                        "& button": {
                          boxShadow: hasPicture ? emotionTheme.tokens["shadow-md"] : void 0,
                          "&:hover": { bgcolor: emotionTheme.tokens["color-action-default-hovered"] }
                        }
                      },
                      children: /* @__PURE__ */ jsx(
                        EzIconButton,
                        {
                          ariaLabel: dismissLabel || t("dismiss"),
                          color: "common.white",
                          onClick: onDismiss,
                          children: /* @__PURE__ */ jsx(EzIcon, { icon: faXmark, color: "common.black" })
                        }
                      )
                    }
                  )
                ]
              }
            )
          }
        ) })
      ]
    }
  ) });
};
export {
  EzModal as default
};
//# sourceMappingURL=EzModal.es.js.map
